import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { Subscription } from 'rxjs';
import { PaymentService } from '../../shared/services/payment.service';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { NotificationService } from '../../shared/services/notification.service';
import { applyImageFallback } from '../../shared/utils/image-fallback';
import { DocumentService } from '../../shared/services/document.service';
import { DocumentPresignedType } from '../../shared/services/api.service';

interface InvoiceItem {
  part_desc: string;
  item_amount: string;
  image_url: string;
  total_quantity?: number;
  available_quantity?: any;
  part_no?: string;
}

interface SellerDetail {
  seller_code: string;
  seller_name: string;
  branch_code: string;
  branch_name: string;
  address_1: string;
  address_2: string;
  address_3: string;
  district: string;
  taluka: string;
  state: string;
  region: string;
  pincode: string;
  city: string;
  is_parent: boolean;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  image_path: string;
}

interface PriceBreakup {
  sub_total: string;
  discount_on_base_price: string;
  platform_charges: string;
  delivery_charges: string;
  gst: string;
  total_amount: string;
}

interface OrderAddress {
  id: number;
  order_id_id: number;
  seller_id: number | null;
  phone_no: string;
  address_1: string;
  address_2: string;
  address_3: string;
  district: string;
  taluka: string;
  state: string;
  pincode: string;
  address_id: number | null;
  created_dt: string;
  update_dt: string;
  is_active: boolean;
  city: string;
  land_mark: string;
  region: string;
  country: string;
  state_code: string | null;
  is_gstin_no: boolean;
  gstin_number: string | null;
  address_type: string;
  address_type_name: string | null;
  receiver_name: string | null;
  receiver_phone_no: string | null;
  company_name: string | null;
}

interface ApiOrder {
  invoice_number: string;
  order_number: string;
  booking_number?: string;
  invoice_status: string;
  delivery_date: string;
  tracking_dt: string;
  delivery_type: string | null;
  order_rating_status: string;
  invoice_rating: number;
  invoice_items: InvoiceItem[];
  invoice_amount: string;
  seller_detail: SellerDetail;
  price_breakup: PriceBreakup;
  order_address: OrderAddress[];
  invoice_pdf_path: string;
}

interface OrderInvoiceDetail {
  invoice_number: string;
  order_number: string;
  part_type: string | null;
  invoice_status: string;
  delivery_type: string | null;
  delivery_date: string | null;
  invoice_amount: number | string | null;
  overall_invoice_amount: number | string | null;
  coupon_amount: number | string | null;
  delivery_charges: number | string | null;
  other_charges: number | string | null;
  total_quantity: number | string | null;
  invoice_pdf_path: string | null;
}

interface Order {
  id: string;
  invoiceNo: string;
  orderNumber: string;
  invoiceNumber: string;
  bookingNumber: string;
  status:
  | 'Order Placed'
  | 'Out for Delivery'
  | 'Delivered'
  | 'Order Ready'
  | 'Order Cancelled'
  | 'Returned'
  | 'Confirmed'
  | 'Payment Failed'
  | 'Payment Pending'
  | 'aborted'
  | 'In-Transit'
  | 'Order Confirmed'
  | 'Picked Up'
  | 'Refund Pending'
  | 'Shipped'
  | 'Return Requested'
  | 'Refund Credited'
  | 'Refund Initiated'
  | 'Booked'
  | 'Pending';

  deliveryType: 'Home Delivery' | 'Store Pickup' | string;
  deliveryDate: string;
  rating?: {
    stars: number;
    text: string;
  };
  items: {
    id: string;
    name: string;
    price: number;
    image: string;
    total_quantity?: number;
    available_quantity?: any;
    part_no?: string;
    part_type?: string;
    inventory_id?: string;
  }[];
  isBooking?: boolean;
  documentType?: string;
  bookingAmount?: number;
  orderTotal?: number;
  bookingPdfPath?: string;
  bookingStatus?: string;
}

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
})
export class OrdersComponent implements OnInit, OnDestroy {
  clearSearch() {
    this.searchTerm = '';
    this.searched = false;
    this.getOrderList();
  }

  // Time filter dropdown
  selectedTimeFilter:
    | 'thisMonth'
    | 'lastMonth'
    | 'last6Month'
    | '2025'
    | '2024'
    | '2023'
    | '' = '';

  // Time dropdown methods
  toggleTimeDropdown(): void {
    this.showTimeDropdown = !this.showTimeDropdown;
  }

  selectTimeFilter(
    filter: 'thisMonth' | 'lastMonth' | 'last6Month' | '2025' | '2024' | '2023',
  ): void {
    this.selectedTimeFilter = filter;
    this.showTimeDropdown = false;
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
  }

  getTimeFilterDisplayText(): string {
    const displayMap: { [key: string]: string } = {
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      last6Month: 'Last 6 Month',
      '2025': '2025',
      '2024': '2024',
      '2023': '2023',
    };
    return displayMap[this.selectedTimeFilter] || 'Time';
  }

  resetTimeFilter(): void {
    this.selectedTimeFilter = '';
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
  }
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private apiService: ApiService,
    public loaderService: LoaderService,
    public paymentService: PaymentService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    public documentService: DocumentService,
  ) {
    this.formBuilder = this.fb;
    // this.router.navigate(['/order']);
  }
  countOrders: any;
  orders: ApiOrder[] = [];
  orderTypes: any[] = ['Home Delivery', 'Store Pickup'];
  selectedTab: 'all' | 'active' | 'completed' | 'booking' | 'enquiry' = 'all';
  selectedOrderType: string = '';
  searchTerm: string = '';
  searched: boolean = false;
  sub: Subscription = new Subscription();

  // Custom dropdown state
  showOrderTypeDropdown: boolean = false;
  selectedProductType: 'both' | 'Scrap Part' | 'Used Part' = 'both';
  selectedOrderStatuses: string[] = [];

  // Time dropdown state
  showTimeDropdown: boolean = false;

  formBuilder: any;
  ccavenue: any;
  currentOrderNumber: string = '';

  currentPageValue = 1;
  currentPage = 1;
  totalPagesValue = 0;
  showPagination = true;
  private readonly defaultLimit = 10;
  private readonly bookingLimit = 20;
  limit = 10;
  offset = 0;

  feedbackChips = {
    poor: {
      question: "What didn't go well with the order?",
      chips: [
        'Wrong Part',
        'Bad Quality',
        'Late Delivery',
        'Website Crash',
        'Confusing Website',
      ],
    },
    good: {
      question: 'What can be improved?',
      chips: [
        'Average Quality',
        'Weak Packaging',
        'Delivery Delay',
        'Website Lag',
        'Slow Support',
      ],
    },
    excellent: {
      question: 'What went really well?',
      chips: [
        'Searching Product',
        'Perfect Fit',
        'Fast Delivery',
        'Smooth Website',
        'Easy Process',
      ],
    },
  };

  showRatingPopup = false;
  selectedRating: number = 0; // Default to 0 (no selection - gray)
  selectedChips: string[] = [];
  reviewComment: string = '';
  RatingOrder: any;
  // Get filtered orders
  ngOnInit(): void {
    this.getOrderList();

    if (isPlatformBrowser(this.platformId)) {
      this.addPageVisibilityListener();
    }
  }

  isLoading = false;

  get filteredOrders(): Order[] {
    const allOrders = this.transformApiOrdersToOrders(this.orders || []);
    if (!allOrders || allOrders.length === 0) {
      return [];
    }

    return allOrders.filter((order) => {
      // 1. Status filter
      if (this.selectedOrderStatuses && this.selectedOrderStatuses.length > 0) {
        const matchesStatus = this.selectedOrderStatuses.some((st) => {
          const sLower = st.toLowerCase().trim();
          const orderStatusLower = (order.status || '').toLowerCase().trim();
          const bookingStatusLower = (order.bookingStatus || '').toLowerCase().trim();
          return (
            orderStatusLower === sLower ||
            orderStatusLower.includes(sLower) ||
            sLower.includes(orderStatusLower) ||
            bookingStatusLower.includes(sLower) ||
            sLower.includes(bookingStatusLower)
          );
        });
        if (!matchesStatus) {
          return false;
        }
      }

      // 2. Search term filter
      if (this.searchTerm && this.searchTerm.trim()) {
        const term = this.searchTerm.trim().toLowerCase();
        const matchesSearch =
          (order.id && order.id.toLowerCase().includes(term)) ||
          (order.invoiceNo && order.invoiceNo.toLowerCase().includes(term)) ||
          (order.orderNumber && order.orderNumber.toLowerCase().includes(term)) ||
          (order.bookingNumber && order.bookingNumber.toLowerCase().includes(term)) ||
          (order.items &&
            order.items.some(
              (item) =>
                (item.name && item.name.toLowerCase().includes(term)) ||
                (item.part_no && item.part_no.toLowerCase().includes(term)),
            ));
        if (!matchesSearch) {
          return false;
        }
      }

      // 3. Time filter
      if (this.selectedTimeFilter && order.deliveryDate) {
        const orderDate = new Date(order.deliveryDate);
        if (!isNaN(orderDate.getTime())) {
          const today = new Date();
          if (this.selectedTimeFilter === 'thisMonth') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            if (orderDate < firstDay || orderDate > today) return false;
          } else if (this.selectedTimeFilter === 'lastMonth') {
            const firstDayLastMonth = new Date(
              today.getFullYear(),
              today.getMonth() - 1,
              1,
            );
            const lastDayLastMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              0,
              23,
              59,
              59,
            );
            if (orderDate < firstDayLastMonth || orderDate > lastDayLastMonth)
              return false;
          } else if (this.selectedTimeFilter === 'last6Month') {
            const sixMonthsAgo = new Date(today);
            sixMonthsAgo.setMonth(today.getMonth() - 6);
            if (orderDate < sixMonthsAgo || orderDate > today) return false;
          } else if (this.selectedTimeFilter === '2025') {
            if (orderDate.getFullYear() !== 2025) return false;
          } else if (this.selectedTimeFilter === '2024') {
            if (orderDate.getFullYear() !== 2024) return false;
          } else if (this.selectedTimeFilter === '2023') {
            if (orderDate.getFullYear() !== 2023) return false;
          }
        }
      }

      return true;
    });
  }


  // Transform API orders to component orders
  private transformApiOrdersToOrders(apiOrders: ApiOrder[]): Order[] {
    if (!Array.isArray(apiOrders)) {
      return [];
    }

    return apiOrders.map((apiOrder) => {
      const lineItems = Array.isArray(apiOrder?.invoice_items)
        ? apiOrder.invoice_items
        : [];
      const orderNumber = apiOrder?.order_number || '';
      const invoiceNumber = apiOrder?.invoice_number || '';
      const bookingNumber =
        apiOrder?.booking_number || (apiOrder as any)?.booking_no || '';
      const orderId = orderNumber || bookingNumber || invoiceNumber;

      const bookingAmount = Number((apiOrder as any)?.booking_amount);
      const totalOrderVal = this.extractPriceFromString(
        (apiOrder as any)?.order_total ??
        (apiOrder as any)?.total_order_amount ??
        (apiOrder as any)?.grand_total ??
        (apiOrder as any)?.total_amount ??
        0,
      );
      const orderDate =
        (apiOrder as any)?.order_date ||
        apiOrder?.tracking_dt ||
        (apiOrder as any)?.booking_date ||
        '';

      const rawCandidates = [
        (apiOrder as any)?.order_status,
        (apiOrder as any)?.booking_status,
        apiOrder?.invoice_status,
        (apiOrder as any)?.status,
      ].map((s) => (s ? String(s).trim() : ''));

      let resolvedStatus = '';
      const cancelledCandidate = rawCandidates.find((s) => /cancel/i.test(s));
      const readyCandidate = rawCandidates.find((s) => /ready/i.test(s));

      if (cancelledCandidate) {
        resolvedStatus = 'Order Cancelled';
      } else if (readyCandidate) {
        resolvedStatus = 'Order Ready';
      } else {
        resolvedStatus =
          (apiOrder as any)?.order_status ||
          (apiOrder as any)?.booking_status ||
          apiOrder?.invoice_status ||
          '';
      }

      return {
        id: orderId,
        invoiceNo:
          this.selectedTab === 'booking' || this.selectedTab === 'enquiry'
            ? bookingNumber || invoiceNumber
            : invoiceNumber || bookingNumber,
        orderNumber,
        invoiceNumber,
        bookingNumber,
        status: this.mapOrderStatus(
          resolvedStatus
        ),
        deliveryType: apiOrder?.delivery_type || 'Home Delivery',
        deliveryDate: this.toDatePipeValue(orderDate),
        rating:
          apiOrder?.invoice_rating > 0
            ? {
              stars: apiOrder?.invoice_rating,
              text: this.getRatingText(apiOrder?.invoice_rating),
            }
            : {
              stars: 0,
              text: 'Post-Delivery',
            },
        items: lineItems.map((item: any, index: number) => ({
          id: `${orderId}-${index}`,
          name: item?.part_desc || item?.part_name || item?.name || '',
          price: this.extractPriceFromString(
            item?.item_amount ?? item?.amount ?? item?.price,
          ),
          image: item?.image_url || item?.image || '',
          total_quantity: item?.total_quantity ?? item?.quantity,
          available_quantity: item?.available_quantity || 0,
          part_no: item?.part_no,
          part_type: item?.part_type,
          inventory_id: item?.inventory_id,
        })),
        isBooking: this.selectedTab === 'booking' || this.selectedTab === 'enquiry',
        documentType: (apiOrder as any)?.document_type || '',
        bookingAmount: Number.isFinite(bookingAmount) ? bookingAmount : 0,
        orderTotal: totalOrderVal > 0 ? totalOrderVal : undefined,
        bookingPdfPath: (apiOrder as any)?.booking_pdf_path || '',
        bookingStatus: (apiOrder as any)?.booking_status || '',
      };
    });
  }

  private mapOrderStatus(apiStatus: string): Order['status'] {
    if (!apiStatus) return 'Pending';
    const s = String(apiStatus).trim().toLowerCase();
    if (s.includes('cancel')) return 'Order Cancelled';
    if (s.includes('ready')) return 'Order Ready';
    if (s.includes('deliver') && !s.includes('out')) return 'Delivered';
    if (s.includes('out for delivery')) return 'Out for Delivery';
    if (s.includes('transit')) return 'In-Transit';
    if (s.includes('ship')) return 'Shipped';
    if (s.includes('picked')) return 'Picked Up';
    if (s.includes('placed')) return 'Order Placed';
    if (s.includes('confirm')) return 'Order Confirmed';
    if (s.includes('book')) return 'Booked';
    if (s.includes('fail')) return 'Payment Failed';
    if (s.includes('pending')) return 'Payment Pending';
    if (s.includes('return')) return 'Return Requested';
    if (s.includes('refund')) return 'Refund Credited';
    const statusMap: { [key: string]: Order['status'] } = {
      Confirmed: 'Confirmed',
      'Order Placed': 'Order Placed',
      'Out for Delivery': 'Out for Delivery',
      Delivered: 'Delivered',
      'Order Ready': 'Order Ready',
      'Order Cancelled': 'Order Cancelled',
      Returned: 'Returned',
      'Payment Failed': 'Payment Failed',
      'Payment Pending': 'Payment Pending',
      aborted: 'aborted',
      'In-Transit': 'In-Transit',
      'Order Confirmed': 'Order Confirmed',
      'Picked Up': 'Picked Up',
      'Refund Pending': 'Refund Pending',
      Shipped: 'Shipped',
      'Return Requested': 'Return Requested',
      'Refund Credited': 'Refund Credited',
      Booked: 'Booked',
      Pending: 'Pending',
    };
    return statusMap[apiStatus] || (apiStatus as Order['status']);
  }

  private extractPriceFromString(priceString: any): number {
    // Handle cases where priceString is not a string
    if (priceString === null || priceString === undefined) {
      return 0;
    }

    // Handle if it's already a number
    if (typeof priceString === 'number') {
      return priceString;
    }

    // Convert to string safely
    let stringValue: string;
    try {
      stringValue = String(priceString);
    } catch (error) {
      console.error('Error converting to string:', priceString, error);
      return 0;
    }

    // Ensure we have a valid string before calling replace
    if (typeof stringValue !== 'string') {
      console.error(
        'stringValue is not a string:',
        stringValue,
        typeof stringValue,
      );
      return 0;
    }

    // Remove ₹ symbol and commas, then convert to number
    const numericValue = stringValue.replace('₹', '').replace(/,/g, '');
    return parseFloat(numericValue) || 0;
  }

  private getRatingText(rating: number): string {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4) return 'Very Good';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3) return 'Average';
    return 'Poor';
  }

  // Select tab method
  selectTab(tab: 'all' | 'active' | 'completed' | 'booking' | 'enquiry') {
    this.selectedTab = tab;
    this.expandedOrderKeys.clear();
    this.limit =
      tab === 'booking' || tab === 'enquiry' ? this.bookingLimit : this.defaultLimit;
    this.offset = 0;
    this.currentPage = 1;
    this.currentPageValue = 1;
    this.getOrderList();
  }

  // Get unique statuses for filter dropdown
  get statuses(): string[] {
    return [
      'all',
      ...Array.from(
        new Set(
          (this.orders || [])
            .map((order) => order.invoice_status)
            .filter((status) => !!status),
        ),
      ),
    ];
  }

  getStatusStyles(status: Order['status'] | string) {
    const styles: Record<string, string> = {
      'Order Placed': 'bg-[#FFF4E0] text-[#EB8700]',
      Shipped: 'bg-[#FFF4E0] text-[#C96F00]',
      'In-Transit': 'bg-[#F8EAF4] text-[#C06CB7]',
      'Out for Delivery': 'bg-[#FFF3CC] text-[#B28500]',
      Delivered: 'bg-[#E3F6EA] text-[#36B548]',
      'Order Ready': 'bg-[#FFF3CC] text-[#B28500]',
      'Picked Up': 'bg-[#E3F6EA] text-[#36B548]',
      'Order Cancelled': 'bg-red-100 text-semantic-red',
      Returned: 'bg-orange-50 text-orange-700',
      Confirmed: 'bg-blue-100 text-blue-600',
      'Payment Failed': 'bg-red-100 text-red-600',
      'Payment Pending': 'bg-yellow-100 text-yellow-600',
      aborted: 'bg-yellow-100 text-yellow-600',
      'Order Confirmed': 'bg-[#FFF4E0] text-[#EB8700]',
      'Refund Pending': 'bg-orange-50 text-orange-700',
      'Return Requested': 'bg-amber-50 text-amber-700',
      'Refund Credited': 'bg-green-50 text-green-700',
      'Refund Initiated': 'bg-gray-300 text-gray-600',
      Booked: 'bg-blue-100 text-blue-600',
      Pending: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-yellow-100 text-yellow-700',
    };
    return styles[status] || 'bg-gray-200 text-gray-600';
  }

  formatPrice(price: number | undefined): string {
    if (!price) return '₹0';
    return `₹${price.toLocaleString('en-IN')}`;
  }

  formatBookingAmount(amount?: number): string {
    const value = Number(amount);
    if (!Number.isFinite(value)) {
      return '₹0.00';
    }
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  onImageError(event: Event): void {
    applyImageFallback(event);
  }

  // Calculate total order value
  getOrderTotal(order: Order): number {
    if (order?.orderTotal && order.orderTotal > 0) {
      return order.orderTotal;
    }
    if (!order?.items?.length) {
      return 0;
    }
    return order.items.reduce((total, item) => total + (item.price || 0), 0);
  }

  viewDetails(orderId: string) {
    // Implement navigation or modal logic
  }

  rateAndReview(orderId: string) {
    // Implement rating modal logic
  }

  moveToCart(order: Order) {
    console.log('Moving order to cart:', order);

    if (!order.items || order.items.length === 0) {
      this.notificationService.error('No items found in the order');
      return;
    }

    // Prepare cart items from order items
    const cartItems = order.items.map((item) => ({
      part_number: item?.inventory_id, // Use part_no if available, otherwise use id
      delivery_type: order.deliveryType, // Default delivery type
      part_type: item?.part_type, // Default part type for order items
      quantity: item?.total_quantity, // Use total_quantity if available, otherwise default to 1
    }));

    const payload = {
      offset: 0,
      limit: 10,
      action_type: 'ADD_DATA',
      validate_request: true,
      source: 'Online',
      part_number_list: cartItems.map((item) => item.part_number),
      cart_items: cartItems,
    };

    this.loaderService.show();

    this.apiService.addToCart(payload).subscribe({
      next: (res) => {
        this.loaderService.hide();
        console.log('Items successfully added to cart:', res);
        // this.notificationService.success('Items added to cart successfully!');
      },
      error: (err) => {
        this.loaderService.hide();
        this.notificationService.error(
          err.error?.data?.msg ||
          'Failed to add items to cart. Please try again.',
        );
      },
    });
  }

  retryPayment(order: Order) {
    // Implement retry payment logic
  }

  // Clear filters
  clearFilters() {
    this.selectedTab = 'all';
    this.searchTerm = '';
    this.expandedOrderKeys.clear();
  }

  private expandedOrderKeys = new Set<string>();

  private getOrderKey(order: Order): string {
    return `${order?.orderNumber ?? ''}|${order?.invoiceNumber ?? ''}|${order?.bookingNumber ?? ''}`;
  }

  isOrderExpanded(order: Order): boolean {
    return this.expandedOrderKeys.has(this.getOrderKey(order));
  }

  private bookingLineItems = new Map<string, any[]>();
  private bookingItemsLoading = new Set<string>();
  private bookingItemsFailed = new Set<string>();
  private invoiceDetails = new Map<string, OrderInvoiceDetail[]>();
  private invoiceDetailsLoading = new Set<string>();
  private invoiceDetailsFailed = new Set<string>();

  toggleOrderExpanded(order: Order): void {
    const key = this.getOrderKey(order);
    if (this.expandedOrderKeys.has(key)) {
      this.expandedOrderKeys.delete(key);
      return;
    }
    this.expandedOrderKeys.add(key);
    if (order?.isBooking) {
      this.loadBookingLineItems(order);
    } else {
      this.loadInvoiceDetails(order);
    }
  }

  private getInvoiceDetailsKey(order: Order): string {
    return `${order?.invoiceNumber || order?.invoiceNo || ''}|${order?.status || ''}`;
  }

  getInvoiceDetails(order: Order): OrderInvoiceDetail[] {
    return this.invoiceDetails.get(this.getInvoiceDetailsKey(order)) ?? [];
  }

  isInvoiceDetailsLoading(order: Order): boolean {
    return this.invoiceDetailsLoading.has(this.getInvoiceDetailsKey(order));
  }

  hasInvoiceDetailsError(order: Order): boolean {
    return this.invoiceDetailsFailed.has(this.getInvoiceDetailsKey(order));
  }

  private loadInvoiceDetails(order: Order): void {
    const invoiceNumber = order?.invoiceNumber || order?.invoiceNo || '';
    const key = this.getInvoiceDetailsKey(order);

    if (
      !invoiceNumber ||
      this.invoiceDetails.has(key) ||
      this.invoiceDetailsLoading.has(key)
    ) {
      return;
    }

    this.invoiceDetailsLoading.add(key);
    this.invoiceDetailsFailed.delete(key);

    const payload = {
      action_type: 'invoice_items',
      invoice_number: invoiceNumber,
      invoice_status: order?.status,
    };

    this.sub.add(
      this.apiService.getorderlist(payload).subscribe({
        next: (res: any) => {
          this.invoiceDetailsLoading.delete(key);

          if (res?.success) {
            const invoiceItems = Array.isArray(res?.data?.invoice_items)
              ? res.data.invoice_items
              : [];

            this.invoiceDetails.set(key, invoiceItems);
          } else {
            this.invoiceDetailsFailed.add(key);
          }

          this.cdr.markForCheck();
        },

        error: () => {
          this.invoiceDetailsLoading.delete(key);
          this.invoiceDetailsFailed.add(key);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  formatInvoiceAmount(amount: number | string | null | undefined): string {
    const value = Number(amount);
    if (!Number.isFinite(value)) {
      return 'Rs. 0.00';
    }
    return `Rs. ${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  private getBookingItemsKey(order: Order): string {
    return order?.bookingNumber || order?.orderNumber || '';
  }

  getBookingLineItems(order: Order): any[] {
    return this.bookingLineItems.get(this.getBookingItemsKey(order)) ?? [];
  }

  isBookingItemsLoading(order: Order): boolean {
    return this.bookingItemsLoading.has(this.getBookingItemsKey(order));
  }

  hasBookingItemsError(order: Order): boolean {
    return this.bookingItemsFailed.has(this.getBookingItemsKey(order));
  }

  private loadBookingLineItems(order: Order): void {
    const bookingNumber = order?.bookingNumber || '';
    const orderNumber = order?.orderNumber || '';
    const key = this.getBookingItemsKey(order);
    if (
      !key ||
      this.bookingLineItems.has(key) ||
      this.bookingItemsLoading.has(key)
    ) {
      return;
    }

    this.bookingItemsLoading.add(key);
    this.bookingItemsFailed.delete(key);

    const isEnquiry =
      order?.documentType === 'ENQUIRY' ||
      this.selectedTab === 'enquiry' ||
      (bookingNumber && bookingNumber.startsWith('ENQ-'));

    const payload: any = {
      action_type: 'detail',
    };

    if (bookingNumber) {
      payload.booking_number = bookingNumber;
    } else {
      payload.order_number = orderNumber;
    }

    if (isEnquiry) {
      payload.document_type = 'ENQUIRY';
    }

    this.sub.add(
      this.apiService
        .getBookingData(payload)
        .subscribe({
          next: (res: any) => {
            this.bookingItemsLoading.delete(key);
            if (res?.success && res?.data) {
              let items: any[] = [];
              if (Array.isArray(res.data.line_items) && res.data.line_items.length > 0) {
                items = res.data.line_items;
              } else if (Array.isArray(res.data.part_details) && res.data.part_details.length > 0) {
                items = res.data.part_details;
              } else if (Array.isArray(res.data.invoice_items) && res.data.invoice_items.length > 0) {
                items = res.data.invoice_items;
              } else if (
                Array.isArray(res.data.booking_list) &&
                res.data.booking_list.length > 0
              ) {
                const booking = res.data.booking_list[0];
                items = Array.isArray(booking?.line_items) && booking.line_items.length > 0
                  ? booking.line_items
                  : Array.isArray(booking?.part_details) && booking.part_details.length > 0
                    ? booking.part_details
                    : Array.isArray(booking?.invoice_items) && booking.invoice_items.length > 0
                      ? booking.invoice_items
                      : [];
              }
              this.bookingLineItems.set(key, items);
            } else {
              this.bookingItemsFailed.add(key);
            }
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            this.bookingItemsLoading.delete(key);
            this.bookingItemsFailed.add(key);
            this.notificationService.error(
              err?.error?.data?.msg ||
              (isEnquiry
                ? 'Failed to load enquiry items.'
                : 'Failed to load booking items.'),
            );
            this.cdr.markForCheck();
          },
        }),
    );
  }

  navigateToOrderDetails(order: Order) {
    const originalOrderData: ApiOrder | undefined = (this.orders || []).find(
      (apiOrder) => {
        if (order?.isBooking) {
          const bookingNo =
            apiOrder?.booking_number || (apiOrder as any)?.booking_no || '';
          if (order?.bookingNumber) {
            return bookingNo === order.bookingNumber;
          }
          return (
            !bookingNo &&
            !!order?.orderNumber &&
            (apiOrder?.order_number ?? '') === order.orderNumber
          );
        }
        return (
          (apiOrder?.order_number ?? '') === order?.orderNumber &&
          (apiOrder?.invoice_number ?? '') === order?.invoiceNumber
        );
      },
    );
    if (originalOrderData) {
      this.router.navigate(['/orders-details'], {
        state: {
          orderData: originalOrderData,
          isBookingFlow: !!order?.isBooking,
          isEnquiry: this.selectedTab === 'enquiry',
        },
      });
    }
  }

  downloadOrderDocument(order: Order, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!order) return;

    let docType: DocumentPresignedType = 'tax_invoice';
    if (order.isBooking) {
      docType = order.documentType === 'PROFORMA' ? 'proforma' : 'booking';
    }

    const invoiceNumber = (order.invoiceNumber || order.invoiceNo || '').trim();
    const bookingNumber = (order.bookingNumber || order.orderNumber || '').trim();

    const data = {
      invoice_number: invoiceNumber,
      booking_number: bookingNumber,
    };

    this.sub.add(
      this.documentService.openDocument(docType, data).subscribe({
        next: () => { },
        error: () => { },
      }),
    );
  }

  private calculateDateRange(): { from_date: string; to_date: string } {
    let from_date = '';
    let to_date = '';
    const today = new Date();

    if (this.selectedTimeFilter === 'thisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      from_date = firstDay.toISOString().slice(0, 10);
      to_date = today.toISOString().slice(0, 10);
    } else if (this.selectedTimeFilter === 'lastMonth') {
      const firstDayLastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1,
      );
      const lastDayLastMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
      );
      from_date = firstDayLastMonth.toISOString().slice(0, 10);
      to_date = lastDayLastMonth.toISOString().slice(0, 10);
    } else if (this.selectedTimeFilter === 'last6Month') {
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      from_date = sixMonthsAgo.toISOString().slice(0, 10);
      to_date = today.toISOString().slice(0, 10);
    } else if (this.selectedTimeFilter === '2025') {
      from_date = '2025-01-01';
      to_date = '2025-12-31';
    } else if (this.selectedTimeFilter === '2024') {
      from_date = '2024-01-01';
      to_date = '2024-12-31';
    } else if (this.selectedTimeFilter === '2023') {
      from_date = '2023-01-01';
      to_date = '2023-12-31';
    }
    return { from_date, to_date };
  }

  getOrderList(): void {
    this.expandedOrderKeys.clear();
    const { from_date, to_date } = this.calculateDateRange();

    if (this.selectedTab === 'booking') {
      this.getBookingList(from_date, to_date);
      return;
    }
    if (this.selectedTab === 'enquiry') {
      this.getEnquiryList(from_date, to_date);
      return;
    }

    this.isLoading = true;
    this.loaderService.show();

    const payload = {
      action_type: 'invoice_list',
      order_tab:
        this.selectedTab === 'active'
          ? 'Active'
          : this.selectedTab === 'completed'
            ? 'Completed'
            : '',
      order_number_search: '',
      search_text: this.searchTerm ? this.searchTerm.trim() : '',
      invoice_date_to: to_date,
      invoice_date_from: from_date,
      from_date: from_date,
      to_date: to_date,
      offset: this.offset,
      limit: this.limit,
      part_type_list:
        this.selectedProductType === 'both' ? [] : [this.selectedProductType],
      invoice_status_list: this.selectedOrderStatuses,
      status_list: this.selectedOrderStatuses,
      order_status_list: this.selectedOrderStatuses,
    };

    this.sub.add(
      this.apiService.getorderlist(payload).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res?.success) {
            this.orders = Array.isArray(res?.data?.order_invoice_list)
              ? res.data.order_invoice_list
              : [];
            this.countOrders = res?.data?.total_count ?? 0;
          } else {
            this.orders = [];
            this.countOrders = 0;
          }
          const total = Number(res?.data?.total_count) || 0;
          this.totalPagesValue = Math.ceil(total / this.limit) || 0;
          this.showPagination = total > this.limit;
          this.currentPageValue = this.offset / this.limit + 1;
          this.loaderService.hide();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.orders = [];
          this.countOrders = 0;
          this.showPagination = false;
          this.notificationService.error(
            err.error?.data?.msg || 'Failed to load orders. Please try again.',
          );
          this.loaderService.hide();
        },
      }),
    );
  }

  private getBookingList(from_date: string = '', to_date: string = ''): void {
    this.isLoading = true;
    this.loaderService.show();
    this.bookingLineItems.clear();
    this.bookingItemsFailed.clear();
    this.expandedOrderKeys.clear();

    const payload: any = {
      action_type: 'list',
      document_type: 'Booking',
      limit: this.limit,
      offset: this.offset,
      search_text: this.searchTerm ? this.searchTerm.trim() : '',
      from_date: from_date,
      to_date: to_date,
      invoice_date_from: from_date,
      invoice_date_to: to_date,
      status_list: this.selectedOrderStatuses,
      invoice_status_list: this.selectedOrderStatuses,
      booking_status_list: this.selectedOrderStatuses,
    };

    this.sub.add(
      this.apiService.getBookingData(payload).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res?.success === false) {
            this.orders = [];
            this.countOrders = 0;
            this.showPagination = false;
            this.totalPagesValue = 0;
            this.notificationService.error(
              res?.data?.msg || 'Failed to load bookings. Please try again.',
            );
            this.loaderService.hide();
            return;
          }

          const { list, total } = this.extractBookingList(res);
          this.orders = list.map((item) => this.normalizeBookingRecord(item));
          this.countOrders = total;
          this.totalPagesValue = Math.ceil(total / this.limit) || 0;
          this.showPagination = total > this.limit;
          this.currentPageValue = this.offset / this.limit + 1;
          this.loaderService.hide();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.orders = [];
          this.countOrders = 0;
          this.showPagination = false;
          this.totalPagesValue = 0;
          this.notificationService.error(
            err.error?.data?.msg ||
            'Failed to load bookings. Please try again.',
          );
          this.loaderService.hide();
        },
      }),
    );
  }

  private getEnquiryList(from_date: string = '', to_date: string = ''): void {
    this.isLoading = true;
    this.loaderService.show();
    this.bookingLineItems.clear();
    this.bookingItemsFailed.clear();
    this.expandedOrderKeys.clear();

    const payload: any = {
      action_type: 'list',
      document_type: 'ENQUIRY',
      limit: this.limit,
      offset: this.offset,
      search_text: this.searchTerm ? this.searchTerm.trim() : '',
      from_date: from_date,
      to_date: to_date,
      invoice_date_from: from_date,
      invoice_date_to: to_date,
      status_list: this.selectedOrderStatuses,
      invoice_status_list: this.selectedOrderStatuses,
      booking_status_list: this.selectedOrderStatuses,
    };

    this.sub.add(
      this.apiService.getBookingData(payload).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res?.success === false) {
            this.orders = [];
            this.countOrders = 0;
            this.showPagination = false;
            this.totalPagesValue = 0;
            this.notificationService.error(
              res?.data?.msg || 'Failed to load enquiries. Please try again.',
            );
            this.loaderService.hide();
            return;
          }

          const { list, total } = this.extractBookingList(res);
          this.orders = list.map((item) => this.normalizeBookingRecord(item));
          this.countOrders = total;
          this.totalPagesValue = Math.ceil(total / this.limit) || 0;
          this.showPagination = total > this.limit;
          this.currentPageValue = this.offset / this.limit + 1;
          this.loaderService.hide();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.orders = [];
          this.countOrders = 0;
          this.showPagination = false;
          this.totalPagesValue = 0;
          this.notificationService.error(
            err.error?.data?.msg ||
            'Failed to load enquiries. Please try again.',
          );
          this.loaderService.hide();
        },
      }),
    );
  }

  private extractBookingList(res: any): { list: any[]; total: number } {
    const data = res?.data ?? {};
    const list = Array.isArray(data.booking_list) ? data.booking_list : [];
    const total =
      Number(data.total_result ?? data.total_count ?? list.length) ||
      list.length;
    return { list, total };
  }

  private normalizeBookingRecord(item: any): ApiOrder {
    return {
      ...item,
      order_number: item?.order_number || '',
      invoice_number: item?.invoice_number || '',
      booking_number: item?.booking_number || item?.booking_no || '',
      invoice_status: item?.order_status || item?.invoice_status || 'Booked',
      delivery_type: item?.delivery_type || '',
      tracking_dt: this.toDatePipeValue(item?.order_date),
      invoice_items: [],
      invoice_rating: 0,
      document_type: item?.document_type || '',
      booking_amount: item?.booking_amount,
      booking_pdf_path: item?.booking_pdf_path || '',
    } as ApiOrder;
  }

  private toDatePipeValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value.includes('T') ? value : value.replace(' ', 'T');
  }

  getDocumentTypeLabel(documentType?: string): string {
    if (!documentType) {
      return '';
    }
    if (documentType === 'PROFORMA') {
      return 'Proforma';
    }
    if (documentType === 'BOOKING') {
      return 'Booking';
    }
    if (documentType === 'ENQUIRY') {
      return 'Enquiry';
    }
    return documentType;
  }

  // onViewBookingClick(order: Order): void {

  //   this.loaderService.show();
  //   const payload = {
  //     booking_number:order?.invoiceNo,
  //   };

  //   this.sub.add(
  //     this.apiService.getPendingBookingAmount(payload).subscribe({
  //       next: (res: any) => {
  //         this.loaderService.hide();
  //         if (res?.success === false) {
  //           this.notificationService.error(
  //             res?.data?.msg || 'Failed to fetch pending booking amount.',
  //           );
  //           return;
  //         }

  //         const pendingAmount =
  //           res?.data?.pending_booking_amount ??
  //           res?.data?.pending_amount ??
  //           res?.data?.amount;

  //         if (pendingAmount !== undefined && pendingAmount !== null) {
  //           this.notificationService.success(
  //             `Pending booking amount: Rs. ${pendingAmount}`,
  //           );
  //         } else {
  //           this.notificationService.success(
  //             res?.data?.msg || 'Pending booking amount fetched successfully.',
  //           );
  //         }
  //       },
  //       error: (err: any) => {
  //         this.loaderService.hide();
  //         this.notificationService.error(
  //           err?.error?.data?.msg || 'Failed to fetch pending booking amount.',
  //         );
  //       },
  //       complete: () => {
  //         this.loaderService.hide();
  //       },
  //     }),
  //   );
  // }

  handlePageChange(page: number) {
    this.currentPageValue = page;
    this.currentPage = page;
    this.offset = (page - 1) * this.limit;
    // this.offset = (page - 1) * this.limit;
    this.getOrderList();
  }

  //   retryPaymentMethod(order: any) : boolean{
  //  const orderData = order?.items?.map((item: any) => item.available_quantity > item.total_quantity);
  //  console.log('orderData', orderData);
  //  if(orderData){
  //   return true;
  //  }
  //  return false;

  //   }

  retryPaymentMethod(order: any): boolean {
    if (!order?.items?.length) return false;

    return order.items.some(
      (item: any) => item.available_quantity >= item.total_quantity,
    );
  }

  initiatePayment(order: any) {
    console.log('11111111111order', order);
    const orderNumber = order?.orderNumber || order?.id;
    if (!orderNumber) {
      this.notificationService.error(
        'Order number not available. Please try again.',
      );
      return;
    }

    // Store the current order number for payment gateway monitoring
    this.currentOrderNumber = orderNumber;

    // Create payment request payload
    const paymentPayload = {
      order_number: orderNumber,
      is_retry_payment: true,
      ...(order?.payment_flow ? { payment_flow: order.payment_flow } : {}),
    };

    this.loaderService.show();
    // Call payment service to get encrypted payment data
    this.sub.add(
      this.paymentService.getEncryptedPaymentData(paymentPayload).subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            console.log('Payment data received:', response.data);
            this.ccavenue = response?.data;
            let encRequest = response?.data;
            let ccavenuForm: FormGroup = this.formBuilder.group({
              encRequest: [encRequest],
              access_code: [this.ccavenue?.access_code],
            });
            this.payWithCcavenu(ccavenuForm);

            this.loaderService.show();
          } else {
            this.notificationService.error(
              response?.data?.msg ||
              'Failed to get payment data. Please try again.',
            );
            this.loaderService.hide();
          }
        },
        error: (error: any) => {
          this.loaderService.hide();

          this.notificationService.error(
            error.error?.data?.msg ||
            'Payment service error. Please try again.',
          );
        },
      }),
    );
  }

  payWithCcavenu(formGroup: FormGroup) {
    console.log(
      'payWithCcavenu method called with formGroup:',
      formGroup.value,
    );
    console.log('ccavenue object:', this.ccavenue);

    // localStorage.removeItem('productOfferData');

    if (!this.ccavenue || !this.ccavenue.ccavenue_url) {
      this.notificationService.error(
        'CCavenue configuration not available. Please try again.',
      );
      return;
    }

    const myform = document.createElement('form');
    myform.method = 'POST';

    // Fix the action URL construction
    const baseUrl = this.ccavenue.ccavenue_url;
    const separator = baseUrl.includes('?') ? '&' : '?';
    myform.action = `${baseUrl}${separator}encRequest=${this.ccavenue.encRequest}&access_code=${this.ccavenue.access_code}`;

    console.log('Form action URL:', myform.action);

    let input: any;
    for (const [key, value] of Object.entries(formGroup.value)) {
      input = document.createElement('input');
      input.name = key;
      input.id = key;
      input.value = value;
      myform.appendChild(input);
    }

    myform.style.display = 'none';

    // Remove invalid append calls - these are not valid for form elements
    // myform.append("Content-Type", "application/x-www-form-urlencoded");
    // myform.append("Accept", "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8");

    document.body.appendChild(myform);
    console.log('Form submitted to:', myform.action);
    myform.submit();
  }

  /**
   * Handle browser back button press
   * This will detect when user navigates back from payment gateway
   */
  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) { }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (this.showOrderTypeDropdown) {
      const dropdownContainer = target.closest('[data-dropdown-container]');
      if (!dropdownContainer) {
        this.showOrderTypeDropdown = false;
      }
    }

    if (this.showTimeDropdown) {
      const timeDropdownContainer = target.closest(
        '[data-time-dropdown-container]',
      );
      if (!timeDropdownContainer) {
        this.showTimeDropdown = false;
      }
    }
  }

  /**
   * Add page visibility listener for better payment gateway back detection
   */
  private addPageVisibilityListener() {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }

  /**
   * Handle visibility change for cleanup
   */
  private handleVisibilityChange = () => {
    console.log('handleVisibilityChange called in orders component');
    // This method is bound to this context for proper cleanup
    if (!document.hidden) {
      console.log('Page became visible - checking for payment gateway return');
      setTimeout(() => { }, 1000);
    }
  };

  submitRating(): void {
    const payload = {
      // order_number: this.RatingOrder.id,
      invoice_number: this.RatingOrder.invoiceNumber,
      invoice_ratings:
        this.selectedRating == 1
          ? 'poor'
          : this.selectedRating == 2
            ? 'average'
            : 'excellent',
      chips: this.selectedChips,
      review: this.reviewComment,
    };

    this.sub.add(
      this.apiService.ratingOrder(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.notificationService.success('Rating submitted successfully!');
            this.getOrderList();
            // Close popup and reset data after successful submission
            this.showRatingPopup = false;
            this.resetRatingData();
          } else {
            this.notificationService.error(
              res.data?.msg || 'Failed to submit rating. Please try again.',
            );
            // Close popup even on failure to prevent it from staying open
            this.showRatingPopup = false;
            this.resetRatingData();
          }
        },
        error: (err) => {
          this.notificationService.error(
            err.error?.data?.msg ||
            'Something went wrong. Please try again later.',
          );
          // Close popup on error to prevent it from staying open
          this.showRatingPopup = false;
          this.resetRatingData();
        },
      }),
    );
  }

  toggleRatingPopup(order?: Order): void {
    this.showRatingPopup = !this.showRatingPopup;
    this.RatingOrder = order;
    if (!this.showRatingPopup) {
      this.resetRatingData();
    }
  }

  resetRatingData(): void {
    this.selectedRating = 0;
    this.selectedChips = [];
    this.reviewComment = '';
  }

  selectRating(rating: number): void {
    this.selectedRating = rating;
    // Clear selected chips when rating changes
    this.selectedChips = [];
  }

  getCurrentFeedbackData(): { question: string; chips: string[] } {
    switch (this.selectedRating) {
      case 1:
        return this.feedbackChips.poor;
      case 2:
        return this.feedbackChips.good;
      case 3:
        return this.feedbackChips.excellent;
      default:
        return this.feedbackChips.excellent;
    }
  }

  // Toggle chip selection
  toggleChip(chip: string): void {
    const index = this.selectedChips.indexOf(chip);
    if (index > -1) {
      this.selectedChips.splice(index, 1);
    } else {
      this.selectedChips.push(chip);
    }
  }

  // Check if chip is selected
  isChipSelected(chip: string): boolean {
    return this.selectedChips.includes(chip);
  }

  searchOrders(): void {
    this.searched = true;
    this.searchTerm = this.searchTerm.trim();
    this.offset = 0;
    this.currentPage = 1;

    console.log('Searching orders with term:', this.searchTerm);
    this.getOrderList();
  }

  onSearchEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.searchTerm.trim()) {
      event.preventDefault();
      this.searchOrders();
    }
  }

  selectOrderType(event: Event): void {
    this.selectedOrderType = (event.target as HTMLSelectElement).value;
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
    console.log('Selected order type:', this.selectedOrderType);
  }

  // Custom dropdown methods
  toggleOrderTypeDropdown(): void {
    this.showOrderTypeDropdown = !this.showOrderTypeDropdown;
  }

  selectProductType(type: 'both' | 'Scrap Part' | 'Used Part'): void {
    this.selectedProductType = type;
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
    console.log('Selected product type:', this.selectedProductType);
  }

  resetProductType(): void {
    this.selectedProductType = 'both';
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
  }

  toggleOrderStatus(status: string): void {
    const index = this.selectedOrderStatuses.indexOf(status);
    if (index > -1) {
      this.selectedOrderStatuses.splice(index, 1);
    } else {
      this.selectedOrderStatuses.push(status);
    }
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
    console.log('Selected order status:', this.selectedOrderStatuses);
  }

  resetOrderStatus(): void {
    this.selectedOrderStatuses = [];
    this.offset = 0;
    this.currentPage = 1;
    this.getOrderList();
  }

  resetAllFilters(): void {
    this.selectedOrderStatuses = [];
    this.selectedTimeFilter = '';
    this.selectedProductType = 'both';
    this.searchTerm = '';
    this.searched = false;
    this.offset = 0;
    this.currentPage = 1;
    this.currentPageValue = 1;
    this.getOrderList();
  }

  getOrderTypeDisplayText(): string {
    // Map status values to display names
    const statusDisplayMap: { [key: string]: string } = {
      // 'Out for Delivery': 'Out for delivery',
      // Delivered: 'Delivered',
      'Order Cancelled': 'Order Cancelled',
      // 'Return Requested': 'Return Requested',
      'Picked Up': 'Picked Up',
      'Refund Pending': 'Refund Pending',
      'Refund Credited': 'Refund Credited',
      // Shipped: 'Shipped',
      // 'In-Transit': 'In-Transit',
    };

    // Handle order status display
    if (this.selectedOrderStatuses.length > 0) {
      if (this.selectedOrderStatuses.length === 1) {
        // Single status selected - show the status name
        const statusName = this.selectedOrderStatuses[0];
        return statusDisplayMap[statusName] || statusName;
      } else {
        // Multiple statuses selected - show count
        return `${this.selectedOrderStatuses.length} Statuses selected`;
      }
    }

    // If no statuses selected, check for product type
    if (this.selectedProductType !== 'both') {
      return (
        this.selectedProductType.charAt(0).toUpperCase() +
        this.selectedProductType.slice(1)
      );
    }

    return 'Order Type';
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }
}
